export type Fp1ContractStatus =
    | 'draft'
    | 'sent'
    | 'signed'
    | 'active'
    | 'expired'
    | 'cancelled'
    | 'renewal_pending';

export type Fp1ContractDiscountType =
    | 'percentage'
    | 'fixed_amount'
    | 'custom_pricing'
    | 'free_devices'
    | 'marketing_partnership';

export interface Fp1ContractStatusHistoryEntry {
    id: number;
    contract_uuid: string;
    old_status: string | null;
    new_status: string;
    changed_by: string | null;
    changed_by_name: string | null;
    notes: string | null;
    changed_at: string;
}

export interface Fp1Contract {
    uuid: string;
    organization_uuid: string | null;
    organization_name: string | null;
    contract_name: string;
    status: Fp1ContractStatus;
    start_date: string | null;
    expiration_date: string | null;
    renewal_reminder_date: string | null;
    auto_renew: boolean;
    internal_notes: string | null;
    custom_terms: string | null;
    discount_type: Fp1ContractDiscountType | null;
    discount_value: string | null;
    max_devices: number | null;
    included_athletes: number | null;
    free_replacements_allowed: number | null;
    signed_pdf_path: string | null;
    has_signed_pdf: boolean;
    created_by: string | null;
    created_by_name: string | null;
    archived_at: string | null;
    created_at: string;
    updated_at: string;
    is_expiring_within_30: boolean;
    is_expiring_within_60: boolean;
    status_history?: Fp1ContractStatusHistoryEntry[];
}

export const CONTRACT_STATUS_LABELS: Record<Fp1ContractStatus, string> = {
    draft: 'Draft',
    sent: 'Sent',
    signed: 'Signed',
    active: 'Active',
    expired: 'Expired',
    cancelled: 'Cancelled',
    renewal_pending: 'Renewal Pending',
};

export const CONTRACT_STATUS_COLORS: Record<
    Fp1ContractStatus,
    'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
    draft: 'default',
    sent: 'info',
    signed: 'primary',
    active: 'success',
    expired: 'error',
    cancelled: 'default',
    renewal_pending: 'warning',
};

export const CONTRACT_DISCOUNT_TYPE_LABELS: Record<Fp1ContractDiscountType, string> = {
    percentage: 'Percentage',
    fixed_amount: 'Fixed Amount',
    custom_pricing: 'Custom Pricing',
    free_devices: 'Free Devices',
    marketing_partnership: 'Marketing Partnership',
};

export const ALL_CONTRACT_STATUSES: Fp1ContractStatus[] = [
    'draft',
    'sent',
    'signed',
    'active',
    'expired',
    'cancelled',
    'renewal_pending',
];

export const ALL_DISCOUNT_TYPES: Fp1ContractDiscountType[] = [
    'percentage',
    'fixed_amount',
    'custom_pricing',
    'free_devices',
    'marketing_partnership',
];

export interface CreateContractParams {
    contract_name: string;
    organization_uuid: string | null;
    organization_name: string | null;
    status: Fp1ContractStatus;
    start_date: string | null;
    expiration_date: string | null;
    renewal_reminder_date: string | null;
    auto_renew: boolean;
    internal_notes: string | null;
    custom_terms: string | null;
    discount_type: Fp1ContractDiscountType | null;
    discount_value: string | null;
    max_devices: number | null;
    included_athletes: number | null;
    free_replacements_allowed: number | null;
}

export type UpdateContractParams = Partial<CreateContractParams> & {
    status_change_notes?: string | null;
};
